import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Template Dashboard 2 MUI components
import TemplateBox from "components/TemplateBox";

// Template Dashboard 2 MUI example components
import Sidenav from "examples/Sidenav";
// Template Dashboard 2 MUI themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Template Dashboard 2 MUI routes
import routes from "routes";

// Template Dashboard 2 MUI contexts
import { useTemplateController } from "context";

// Images
import brand from "assets/images/logos/LOGO-EptaInternal_vert_pos_CMYK.png";
import brandDark from "assets/images/logos/LOGO-EptaInternal_vert_neg_CMYK.png";

// Icon Fonts
import "assets/css/nucleo-icons.css";
import "assets/css/nucleo-svg.css";

import Dashboard from "layouts/dashboard"
import { Hub } from "aws-amplify/utils";
import { signInWithRedirect, getCurrentUser } from "aws-amplify/auth";
import SSOSignIn from "layouts/authentication/sso-sign-in";
import { I18n } from "@aws-amplify/core";
import { translations } from "translation/translations";

I18n.putVocabularies(translations);
I18n.setLanguage("en")
export default function App() {
  const projectEnvironment = process.env.REACT_APP_ENV_ENVIRONMENT;
  const oidcProvider = {
    dev:"AZURE-OIDC",
    qa:"AZURE-OIDC",
    prod:"AZURE-OIDC"
  }
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [customState, setCustomState] = useState(null);
  const navigate = useNavigate()

  useEffect(() => {
    const getUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          navigate("/sign-in");
        }
        else if (currentUser && window.location.pathname === "/sign-in") {
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Failed to get current user:", error);
        navigate("/sign-in");
      }
    };

    getUser();
  }, [getCurrentUser, navigate]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          getUser();
          break;
        case "signInWithRedirect_failure":
          setError("An error has ocurred during the OAuth flow.");
          break;
        case "customOAuthState":
          setCustomState(payload.data); // this is the customState provided on signInWithRedirect function
          break;
      }
    });

    getUser();

    return unsubscribe;
  }, []);

  const getUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error(error);

    }
  };

  const [controller, dispatch] = useTemplateController();
  const { direction, layout, sidenavColor, darkSidenav, darkMode } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={darkSidenav || darkMode ? brand : brandDark}
              brandName="Epta OrgChart"
              routes={routes}
            />
          </>
        )}
        <Routes>
          {getRoutes(routes)}

          <Route path="*" element={<Dashboard />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={darkSidenav || darkMode ? brand : brandDark}
            brandName="Epta OrgChart"
            routes={routes}
          />
        </>
      )}
      <Routes>
        {getRoutes(routes)}

        <Route path="/" element={<Navigate to="/dashboard" />} />
        {/* Using the project in a local environment the selected OIDC provider is the dev.*/}
        <Route path="/sign-in" element={<SSOSignIn ssoSignIn={() => signInWithRedirect({ provider: { custom: projectEnvironment ? oidcProvider[projectEnvironment]: oidcProvider.dev }, customState: "shopping-cart" })} />} />
      </Routes>
    </ThemeProvider>
  )
}
 